import * as Sentry from '@sentry/react';
import { getImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React from 'react';

import { propTypesDataGridWithGrades } from './GridWithGrades.proptypes';

import Picture from '../../../../ui/Atoms/Picture';
import Text, { TextAppearance } from '../../../../ui/Atoms/Text';

import { getStaticUrl } from '../../../utils/getStaticUrl';

import * as S from './GridWithGrades.styles';

const renderStars = stars => {
  const numStars = parseInt(stars, 10);
  if (isNaN(numStars)) {
    return null;
  }

  return Array.from({ length: numStars }, (_, index) => (
    <S.Star key={index} role='img' aria-label='star'>
      ⭐️
    </S.Star>
  ));
};

const renderGrades = ({ criterias, grades }) => {
  const nbCriterias = ['first', 'second', 'third', 'fourth'];

  return (
    <>
      {nbCriterias.map((number, index) => {
        return (
          <S.GradeWrapper key={index}>
            <S.GradeLabel>{`${criterias[`${number}Criteria`]}: ${
              grades[`${number}Grade`]
            }/5`}</S.GradeLabel>
            <S.StarWrapper>
              {renderStars(grades[`${number}Grade`])}
            </S.StarWrapper>
          </S.GradeWrapper>
        );
      })}
    </>
  );
};

export const GridWithGrades = ({ slice }) => {
  if (!slice) {
    return null;
  }

  const data = slice.primary || {};
  const {
    grid_with_grades_title: title,
    first_criteria: firstCriteria,
    second_criteria: secondCriteria,
    third_criteria: thirdCriteria,
    fourth_criteria: fourthCriteria,
  } = data || '';

  const criterias = {
    firstCriteria,
    secondCriteria,
    thirdCriteria,
    fourthCriteria,
  };

  const items = slice.items;
  if (items?.length < 1) {
    Sentry.captureMessage(
      'Error: missing required data in GridWithGrades slice'
    );
    return null;
  }

  return (
    <S.Wrapper>
      <S.StyledHead>
        <S.StyledRow>
          <S.StyledTitle colSpan={'3'}>{title}</S.StyledTitle>
        </S.StyledRow>
      </S.StyledHead>

      <S.StyledBody>
        {items.map((item, index) => {
          const {
            name_with_links: { html: name },
            logo: { alt, localFile },
            first_criteria_grade: firstGrade,
            second_criteria_grade: secondGrade,
            third_criteria_grade: thirdGrade,
            fourth_criteria_grade: fourthGrade,
            first_column_content: { html: firstContent },
            last_column_content: { html: lastContent },
          } = item || '';

          const grades = {
            firstGrade,
            secondGrade,
            thirdGrade,
            fourthGrade,
          };

          const imageProps = {
            imageObject: getImage(localFile),
            imageUrl: getStaticUrl(localFile?.publicURL),
            altText: alt,
            isNotLazy: false,
          };

          return (
            <S.StyledRow key={index}>
              <S.SimpleColumn>
                {imageProps.imageUrl && (
                  <S.PictureWrapper>
                    <Picture {...imageProps} />
                  </S.PictureWrapper>
                )}
                {firstContent && (
                  <Text type={TextAppearance.INFO}>{firstContent}</Text>
                )}
              </S.SimpleColumn>

              <S.GradeColumn>
                {renderGrades({ criterias, grades })}
              </S.GradeColumn>

              <S.SimpleColumn>
                <S.Description>
                  {name && <Text type={TextAppearance.NORMAL}>{name}</Text>}
                  {lastContent && (
                    <Text type={TextAppearance.INFO}>{lastContent}</Text>
                  )}
                </S.Description>
              </S.SimpleColumn>
            </S.StyledRow>
          );
        })}
      </S.StyledBody>
    </S.Wrapper>
  );
};

GridWithGrades.propTypes = {
  /**
   * Content of the SimpleGrid slice.
   */
  slice: PropTypes.shape(propTypesDataGridWithGrades),
};
